import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, inject } from '@angular/core';
import { Dimensions, DomHelper, MathHelper } from '@newroom-connect/library/helpers';

@Directive({
  standalone: true,
  selector: '[nrcOverlayPortal]'
})
export class OverlayPortalDirective implements AfterViewInit, OnDestroy {
  @Input('nrcOverlayPortal') public triggerElement!: HTMLElement;
  @Input() public overlayRoot?: HTMLElement;
  @Input() public overflowParent?: HTMLElement;
  @Input() public offset = 4;
  @Input() public matchWidth = false;
  @Input() public minEdgeDistance = 10;
  @Input() public portalOnly = false;
  @Input() public centerHorizontally = false;

  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private appendedRoot: HTMLElement | null = null;
  private mutationObserver: MutationObserver | null = null;

  /**
   * Initialize the overlay portal.
   */
  public ngAfterViewInit(): void {
    const element = this.elementRef.nativeElement;
    const root = this.overlayRoot ?? document.body;

    this.appendedRoot = root;
    root.appendChild(element);

    if (this.portalOnly) {
      return;
    }

    // Hide element initially to prevent flickering, then show after positioning
    DomHelper.setStyles(element, { position: 'fixed', zIndex: '9999', visibility: 'hidden' });

    // Set up a MutationObserver to reposition when visibility changes (hidden -> visible)
    this.setupVisibilityObserver(element);

    // Schedule positioning after DOM is stable
    this.schedulePositioning();
  }

  /**
   * Clean up the portaled element when the directive is destroyed.
   */
  public ngOnDestroy(): void {
    // Clean up the MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    const element = this.elementRef.nativeElement;

    if (!element || !element.parentNode || !this.appendedRoot) {
      return;
    }

    this.appendedRoot.removeChild(element);
  }

  /**
   * Schedule positioning with a small delay to ensure DOM is stable.
   */
  private schedulePositioning(): void {
    setTimeout(() => this.positionMenu(), 0);
  }

  /**
   * Set up a MutationObserver to watch for class changes and reposition when menu becomes visible.
   *
   * @param element The element to observe.
   */
  private setupVisibilityObserver(element: HTMLElement): void {
    this.mutationObserver = new MutationObserver((mutations: MutationRecord[]): void => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Check if the element just became visible (has 'visible' class or lost 'hidden' class)
          const hasVisible = element.classList.contains('visible');
          const hasHidden = element.classList.contains('hidden');

          if (hasVisible && !hasHidden) {
            // Menu just became visible - reposition it
            this.schedulePositioning();
          }
        }
      }
    });

    this.mutationObserver.observe(element, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Position the menu based on the trigger element and available space.
   */
  private positionMenu(): void {
    if (!this.triggerElement) {
      return;
    }

    const element = this.elementRef.nativeElement;
    const triggerRect = this.triggerElement.getBoundingClientRect();
    const menuDimensions = this.getMenuDimensions(element, triggerRect);

    if (this.matchWidth) {
      element.style.width = `${triggerRect.width}px`;
    }

    const shouldOpenAbove = this.shouldOpenAbove(triggerRect, menuDimensions.height);
    const top = this.calculateVerticalPosition(triggerRect, menuDimensions.height, shouldOpenAbove);
    const left = this.calculateHorizontalPosition(triggerRect, menuDimensions.width);

    const constrainedPosition = this.constrainToViewport(top, left, menuDimensions);

    // Apply position and make visible (was hidden initially to prevent flickering)
    DomHelper.setStyles(element, {
      left: `${constrainedPosition.left}px`,
      top: `${constrainedPosition.top}px`,
      visibility: 'visible'
    });
  }

  /**
   * Get the menu dimensions, with fallbacks for hidden elements.
   *
   * @param element The overlay menu element to measure.
   * @param triggerRect Bounding rectangle of the trigger button for fallback width.
   *
   * @returns Object containing the calculated width and height of the menu.
   */
  private getMenuDimensions(element: HTMLElement, triggerRect: DOMRect): Dimensions {
    const rect = element.getBoundingClientRect();

    return {
      width: rect.width || triggerRect.width,
      height: rect.height || element.scrollHeight
    };
  }

  /**
   * Determine if the menu should open above the trigger based on available space.
   *
   * @param triggerRect Bounding rectangle of the trigger button.
   * @param menuHeight Calculated height of the menu in pixels.
   *
   * @returns True if the menu should open above the trigger, false for below.
   */
  private shouldOpenAbove(triggerRect: DOMRect, menuHeight: number): boolean {
    const parentRect = this.overflowParent?.getBoundingClientRect();
    const parentTop = parentRect?.top ?? 0;
    const parentBottom = parentRect?.bottom ?? window.innerHeight;

    const spaceBelow = parentBottom - triggerRect.bottom;
    const spaceAbove = triggerRect.top - parentTop;
    const requiredSpace = menuHeight + this.offset;

    return spaceBelow < requiredSpace && spaceAbove >= requiredSpace;
  }

  /**
   * Calculate the vertical (top) position of the menu.
   *
   * @param triggerRect Bounding rectangle of the trigger button.
   * @param menuHeight Calculated height of the menu in pixels.
   * @param openAbove When true, positions menu above trigger; when false, below.
   *
   * @returns The calculated top position in pixels.
   */
  private calculateVerticalPosition(triggerRect: DOMRect, menuHeight: number, openAbove: boolean): number {
    return openAbove
      ? triggerRect.top - menuHeight - this.offset
      : triggerRect.bottom + this.offset;
  }

  /**
   * Calculate the horizontal (left) position of the menu.
   *
   * @param triggerRect Bounding rectangle of the trigger button.
   * @param menuWidth Calculated width of the menu in pixels.
   *
   * @returns The calculated left position in pixels.
   */
  private calculateHorizontalPosition(triggerRect: DOMRect, menuWidth: number): number {
    if (this.centerHorizontally) {
      const triggerCenter = triggerRect.left + (triggerRect.width / 2);

      return triggerCenter - (menuWidth / 2);
    }

    return triggerRect.left;
  }

  /**
   * Constrain the menu position to stay within viewport bounds.
   * Only constrains when menu dimensions are smaller than viewport (valid measurements).
   *
   * @param top Initial calculated top position before constraints.
   * @param left Initial calculated left position before constraints.
   * @param menuDimensions Menu size data for boundary calculations.
   *
   * @returns Object with constrained top and left positions within viewport.
   */
  private constrainToViewport(top: number, left: number, menuDimensions: Dimensions): { top: number; left: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let constrainedLeft = left;
    let constrainedTop = top;

    // Only constrain horizontally if menu width is reasonable (smaller than viewport)
    if (menuDimensions.width < viewportWidth) {
      const maxLeft = viewportWidth - menuDimensions.width - this.minEdgeDistance;

      constrainedLeft = MathHelper.clamp(left, this.minEdgeDistance, Math.max(maxLeft, this.minEdgeDistance));
    }

    // Only constrain vertically if menu height is reasonable (smaller than viewport)
    if (menuDimensions.height < viewportHeight) {
      const maxTop = viewportHeight - menuDimensions.height - this.minEdgeDistance;

      constrainedTop = MathHelper.clamp(top, this.minEdgeDistance, Math.max(maxTop, this.minEdgeDistance));
    }

    return {
      left: constrainedLeft,
      top: constrainedTop
    };
  }
}
